/**
 * Nexus API Service Layer
 * Handles IAM-authenticated requests to the Nexus REST API
 */

import { AwsClient } from 'aws4fetch';
import {
  fetchTemporarySigV4Credentials,
  getUserAlias,
} from '../auth/midway';
import { getEnvironmentConfig } from '../configurations/config';
import {
  ControlMapping,
  MappingWithReviews,
  MappingReview,
  MappingFeedback,
  Framework,
  FrameworkControl,
  MappingQueryParams,
  ReviewQueryParams,
  DynamoDBQueryResponse,
  MappingStatus,
  FrameworkStatus,
  ControlStatus,
  FeedbackStatus,
  ControlDomain,
  ControlObjective,
  CommonControl,
  CommonControlsHierarchyResponse,
  ControlHierarchy,
} from '../types/dynamodb';

// API Response Types
export interface ApiResponse<T> {
  statusCode: number;
  body: T;
}

export interface ListMappingsResponse {
  mappings: ControlMapping[];
  count: number;
  nextToken?: string;
}

export interface ListFrameworksResponse {
  frameworks: Framework[];
  count: number;
  nextToken?: string;
}

export interface ListControlsResponse {
  controls: FrameworkControl[];
  count: number;
  nextToken?: string;
}

export interface MappingsForControlResponse {
  controlId: string;
  mappings: ControlMapping[];
  count: number;
  nextToken?: string;
}

// API Error Types
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly endpoint: string,
    public readonly originalError?: Error
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Nexus API Service Class
 * Provides methods for calling Nexus REST APIs with IAM authentication
 */
export class NexusApiService {
  private readonly apiUrl: string;
  private readonly region: string = 'us-east-1'; // Nexus API is deployed in us-east-1
  private static readonly COMMON_CONTROLS_FRAMEWORK_NAME = 'Common-Controls';
  private static readonly COMMON_CONTROLS_FRAMEWORK_VERSION = '1.0';
  private static readonly DEFAULT_PAGE_SIZE = 50;
  private static readonly CONTROL_FETCH_BATCH_SIZE = 10;
  private static readonly FEEDBACK_FETCH_BATCH_SIZE = 10;

  constructor() {
    const config = getEnvironmentConfig();
    this.apiUrl = config.nexusApiUrl;
  }

  /**
   * Sign and send an HTTP request using AWS Signature V4
   * Uses aws4fetch library (same pattern as bindleAuthorization.ts)
   */
  private async signedFetch(
    method: string,
    path: string,
    queryParams?: Record<string, string>,
    body?: any
  ): Promise<any> {
    // Get Midway credentials for SigV4 signing
    let credentials;
    let userAlias = '';
    try {
      const midwayCredentials = await fetchTemporarySigV4Credentials();
      if (
        !midwayCredentials.AccessKeyId ||
        !midwayCredentials.SecretAccessKey
      ) {
        throw new Error('Invalid credentials');
      }

      credentials = {
        accessKeyId: midwayCredentials.AccessKeyId,
        secretAccessKey: midwayCredentials.SecretAccessKey,
        sessionToken: midwayCredentials.SessionToken,
      };
      userAlias = midwayCredentials.MidwayUser || '';
    } catch (error) {
      throw new ApiError(
        'Failed to retrieve Midway credentials',
        401,
        path,
        error as Error
      );
    }
    const userEmail = userAlias?.includes('@') ? userAlias : undefined;

    // Create AWS client for signing requests to API Gateway
    const aws = new AwsClient({
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
      region: this.region,
      service: 'execute-api',
    });

    // Build URL with query parameters
    const url = new URL(`${this.apiUrl}${path}`);
    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value);
        }
      });
    }

    // Sign and send the request
    const headers: Record<string, string> = {};
    if (body) {
      headers['Content-Type'] = 'application/json';
    }
    if (userEmail) {
      headers['X-User-Email'] = userEmail;
    }

    const response = await aws.fetch(url.toString(), {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new ApiError(
        `API request failed: ${response.statusText}`,
        response.status,
        path,
        new Error(errorBody)
      );
    }

    return response.json();
  }

  // ==========================================================================
  // Normalization Helpers
  // ==========================================================================

  private normalizeMappingStatus(status?: string): MappingStatus {
    if (!status) {
      return status as MappingStatus;
    }

    const normalized = status.toLowerCase();
    switch (normalized) {
      case 'labeling_in_progress':
      case 'labeling-in-progress':
      case 'pending_review':
      case 'pending-review':
      case 'pending':
        return 'pending';
      case 'review_in_progress':
      case 'review-in-progress':
      case 'in-review':
        return 'in-review';
      case 'approved':
        return 'approved';
      case 'rejected':
        return 'rejected';
      case 'archived':
      case 'locked':
        return 'locked';
      default:
        return normalized as MappingStatus;
    }
  }

  private toApiMappingStatus(status?: MappingStatus | string): string | undefined {
    if (!status) {
      return undefined;
    }

    const normalized = status.toLowerCase();
    switch (normalized) {
      case 'active':
        return 'ACTIVE';
      case 'approved':
      case 'rejected':
      case 'pending':
      case 'in-review':
      case 'labeling_in_progress':
      case 'pending_review':
      case 'review_in_progress':
        // Backend GetMappings currently only accepts lifecycle filter values
        // (ACTIVE/ARCHIVED). Review-state filtering is applied client-side.
        return 'ACTIVE';
      case 'locked':
      case 'archived':
        return 'ARCHIVED';
      default:
        if (status === 'ACTIVE' || status === 'ARCHIVED') {
          return status;
        }
        return undefined;
    }
  }

  private toApiLifecycleStatus(status?: string): string | undefined {
    if (!status) {
      return undefined;
    }
    const normalized = status.toLowerCase();
    if (normalized === 'active') {
      return 'ACTIVE';
    }
    if (normalized === 'archived') {
      return 'ARCHIVED';
    }
    return status;
  }

  private normalizeFrameworkStatus(status?: string): FrameworkStatus {
    if (!status) {
      return status as FrameworkStatus;
    }
    const normalized = status.toLowerCase();
    switch (normalized) {
      case 'active':
        return 'active';
      case 'archived':
        return 'archived';
      default:
        return normalized as FrameworkStatus;
    }
  }

  private normalizeControlStatus(status?: string): ControlStatus {
    if (!status) {
      return status as ControlStatus;
    }
    const normalized = status.toLowerCase();
    switch (normalized) {
      case 'active':
        return 'active';
      case 'archived':
        return 'archived';
      default:
        return normalized as ControlStatus;
    }
  }

  private buildControlKey(
    frameworkName?: string,
    frameworkVersion?: string,
    controlId?: string
  ): string {
    if (frameworkName && frameworkVersion && controlId) {
      return `${frameworkName}#${frameworkVersion}#${controlId}`;
    }
    return controlId || '';
  }

  private normalizeMapping(mapping: any): ControlMapping {
    if (!mapping) {
      return mapping as ControlMapping;
    }

    const source = mapping.SourceControl || mapping.sourceControl;
    const target = mapping.TargetControl || mapping.targetControl;
    const sourceFrameworkName = source?.FrameworkName || source?.frameworkName;
    const sourceFrameworkVersion = source?.FrameworkVersion || source?.frameworkVersion;
    const sourceControlId = source?.ControlId || source?.controlId;
    const targetFrameworkName = target?.FrameworkName || target?.frameworkName;
    const targetFrameworkVersion = target?.FrameworkVersion || target?.frameworkVersion;
    const targetControlId = target?.ControlId || target?.controlId;
    const controlKey =
      mapping.controlKey ||
      mapping.ControlKey ||
      this.buildControlKey(sourceFrameworkName, sourceFrameworkVersion, sourceControlId);
    const mappedControlKey =
      mapping.mappedControlKey ||
      mapping.MappedControlKey ||
      this.buildControlKey(targetFrameworkName, targetFrameworkVersion, targetControlId);
    const mappingKey =
      mapping.mappingKey ||
      mapping.MappingId ||
      mapping.mappingId ||
      mapping.id ||
      (controlKey && mappedControlKey ? `${controlKey}#${mappedControlKey}` : '');
    const timestamp =
      mapping.timestamp ||
      mapping.CreateTimestamp ||
      mapping.createTimestamp ||
      mapping.SubmissionTimestamp ||
      mapping.submittedAt ||
      new Date().toISOString();
    const sourceFramework =
      mapping.sourceFramework ||
      (sourceFrameworkName && sourceFrameworkVersion
        ? `${sourceFrameworkName}#${sourceFrameworkVersion}`
        : undefined);
    const targetFramework =
      mapping.targetFramework ||
      (targetFrameworkName && targetFrameworkVersion
        ? `${targetFrameworkName}#${targetFrameworkVersion}`
        : undefined);
    const createdBy = mapping.createdBy || mapping.CreatedBy?.Alias || mapping.CreatedBy?.alias;
    const lastModifiedBy =
      mapping.lastModifiedBy || mapping.LastModifiedBy?.Alias || mapping.LastModifiedBy?.alias;

    return {
      ...mapping,
      mappingKey,
      mappingWorkflowKey: mapping.mappingWorkflowKey || mapping.MappingWorkflowKey || '',
      controlKey,
      mappedControlKey,
      status: this.normalizeMappingStatus(mapping.status || mapping.MappingStatus),
      timestamp,
      sourceFramework,
      targetFramework,
      createdBy,
      lastModifiedBy,
    };
  }

  private normalizeFramework(framework: any): Framework {
    if (!framework) {
      return framework as Framework;
    }
    const info = framework.FrameworkInfo || framework.frameworkInfo || {};
    const frameworkName = framework.frameworkName || framework.FrameworkName;
    const version = framework.version || framework.FrameworkVersion;
    const status = framework.status || framework.Status || info.Status;
    return {
      ...framework,
      frameworkName: frameworkName || '',
      version: version || '',
      frameworkKey:
        framework.frameworkKey || (frameworkName && version ? `${frameworkName}#${version}` : ''),
      status: this.normalizeFrameworkStatus(status),
      description: framework.description || info.Description || framework.Description,
      publishedDate: framework.publishedDate || framework.CreateTimestamp || info.CreateTimestamp,
      effectiveDate: framework.effectiveDate,
      expirationDate: framework.expirationDate,
      publisher: framework.publisher || info.Source || framework.Source,
      category: framework.category,
      tags: framework.tags,
      metadata: framework.metadata || info.AdditionalInfo || framework.AdditionalInfo,
      controlCount: framework.controlCount,
    };
  }

  private normalizeFrameworkControl(
    control: any,
    frameworkName?: string,
    frameworkVersion?: string
  ): FrameworkControl {
    if (!control) {
      return control as FrameworkControl;
    }
    const controlId = control.controlId || control.ControlId || control.id || '';
    const frameworkKey =
      control.frameworkKey ||
      (frameworkName && frameworkVersion ? `${frameworkName}#${frameworkVersion}` : '');
    const controlKey =
      control.controlKey ||
      this.buildControlKey(frameworkName, frameworkVersion, controlId) ||
      control.ControlKey ||
      controlId;
    const status = control.status || control.ControlStatus;
    const additionalInfo =
      control.additionalInfo ||
      control.AdditionalInfos ||
      control.AdditionalInfo ||
      control.additionalInfos;
    const metadata =
      control.metadata || (control.ControlGuide ? { controlGuide: control.ControlGuide } : undefined);

    return {
      ...control,
      frameworkKey,
      controlKey,
      controlId,
      status: this.normalizeControlStatus(status),
      title: control.title || control.Title || '',
      description: control.description || control.Description || '',
      documentationUrl: control.documentationUrl || control.DocumentationUrl,
      category: control.category,
      subcategory: control.subcategory,
      priority: control.priority,
      implementationGuidance: control.implementationGuidance,
      relatedControls: control.relatedControls,
      tags: control.tags,
      metadata,
      additionalInfo,
    };
  }

  private normalizeReviewDecision(review: any): string | undefined {
    const decision = review?.Decision || review?.decision;
    const isFinalReview = review?.IsFinalReview ?? review?.isFinalReview;
    if (typeof decision === 'string') {
      const normalized = decision.toLowerCase();
      if (normalized.includes('final')) {
        return normalized;
      }
      if (normalized === 'approve' || normalized === 'reject') {
        return isFinalReview ? `final-${normalized}` : normalized;
      }
      return normalized;
    }
    if (typeof review?.correct === 'boolean') {
      const normalized = review.correct ? 'approve' : 'reject';
      return isFinalReview ? `final-${normalized}` : normalized;
    }
    return undefined;
  }

  private normalizeMappingReview(review: any, mapping?: ControlMapping): MappingReview {
    const feedback = review?.Feedback || review?.feedback || {};
    const submittedAt =
      review?.SubmissionTimestamp ||
      review?.submittedAt ||
      new Date().toISOString();
    const reviewerId =
      review?.ReviewerId ||
      review?.reviewerId ||
      feedback?.reviewerId ||
      'unknown';
    const reviewKey =
      review?.ReviewId ||
      review?.reviewId ||
      review?.reviewKey ||
      `${reviewerId}#${submittedAt}`;

    const submittedByRaw = review?.submittedBy || review?.SubmittedBy;
    let submittedBy = submittedByRaw;
    const entityType = submittedByRaw?.EntityType || submittedByRaw?.entityType;
    if (submittedByRaw && !submittedByRaw.type && entityType) {
      const normalizedType =
        entityType.toLowerCase() === 'human'
          ? 'Human'
          : entityType.toLowerCase() === 'machine'
            ? 'Machine'
            : entityType;
      submittedBy = {
        type: normalizedType,
        userId: submittedByRaw.Alias || submittedByRaw.alias,
        role: submittedByRaw.Role || submittedByRaw.role,
        machineDetails: submittedByRaw.MachineDetails || submittedByRaw.machineDetails,
        timestamp: submittedAt,
      };
    }
    if (!submittedBy || !submittedBy.type) {
      submittedBy = {
        type: 'System',
        timestamp: submittedAt,
      };
    }

    return {
      mappingKey:
        review?.MappingId ||
        review?.mappingKey ||
        mapping?.mappingKey ||
        '',
      reviewKey,
      controlKey: review?.controlKey || mapping?.controlKey || '',
      mappedControlKey: review?.mappedControlKey || mapping?.mappedControlKey || '',
      reviewerId,
      submittedAt,
      status:
        review?.Status ||
        review?.status ||
        mapping?.status ||
        'REVIEW_IN_PROGRESS',
      submittedBy,
      decision: this.normalizeReviewDecision(review) as any,
      justification: feedback?.justification || feedback?.Comment || feedback?.comment,
      reviewerName: feedback?.reviewerName || review?.reviewerName || reviewerId,
      reviewerEmail: feedback?.reviewerEmail || review?.reviewerEmail,
      confidence: review?.confidence,
      tags: review?.tags,
      additionalInfo: review?.additionalInfo || feedback?.additionalInfo || feedback,
    };
  }

  private normalizeFeedbackText(feedback: unknown): string | undefined {
    if (!feedback) {
      return undefined;
    }
    if (typeof feedback === 'string') {
      return feedback;
    }
    if (typeof feedback === 'object') {
      const asRecord = feedback as Record<string, any>;
      return (
        asRecord.justification ||
        asRecord.Comment ||
        asRecord.comment ||
        asRecord.feedback ||
        JSON.stringify(asRecord)
      );
    }
    return String(feedback);
  }

  private normalizeFeedbackLabel(label?: string): string | undefined {
    if (!label) {
      return label;
    }
    const normalized = label.toLowerCase();
    if (normalized === 'positive' || normalized === 'thumbs_up' || normalized === 'thumbs-up') {
      return 'thumbs_up';
    }
    if (normalized === 'negative' || normalized === 'thumbs_down' || normalized === 'thumbs-down') {
      return 'thumbs_down';
    }
    return label;
  }

  private normalizeMappingFeedback(item: any): MappingFeedback {
    const details = item?.Feedback || item?.feedback || {};
    const reviewerId =
      item?.FeedbackProviderId ||
      item?.feedbackProviderId ||
      item?.reviewerId ||
      'unknown';
    const timestamp =
      item?.SubmissionTimestamp ||
      item?.submittedAt ||
      item?.LastModifiedAt ||
      item?.updatedAt ||
      new Date().toISOString();

    return {
      mappingKey: item?.MappingId || item?.mappingKey || '',
      reviewerId,
      timestamp,
      justification: details?.Comment || details?.comment || '',
      isEdgeCaseMapping: details?.isEdgeCaseMapping ?? false,
      reviewerName: details?.reviewerName || reviewerId,
      status: (details?.status as FeedbackStatus) || 'submitted',
      reviewerEmail: details?.reviewerEmail,
      additionalNotes: details?.additionalNotes,
      attachments: details?.attachments,
    };
  }

  private async listFeedbacksRaw(
    mappingId: string,
    params?: { maxResults?: number; nextToken?: string }
  ): Promise<any> {
    const queryParams: Record<string, string> = {};
    if (params?.maxResults) {
      queryParams.maxResults = params.maxResults.toString();
    }
    if (params?.nextToken) {
      queryParams.nextToken = params.nextToken;
    }

    const response = await this.signedFetch(
      'GET',
      `/v1/feedback/${encodeURIComponent(mappingId)}/feedbacks`,
      queryParams
    );
    return response.body || response;
  }

  // ==========================================================================
  // Mappings API
  // ==========================================================================

  /**
   * GET /v1/mapping/getMappings?frameworkName={}&frameworkVersion={}&controlId={}&mappingStatus={}&nextToken={}&maxResults={}
   */
  async listMappings(params?: MappingQueryParams): Promise<ListMappingsResponse> {
    const queryParams: Record<string, string> = {};

    if (params?.frameworkKey) {
      const [frameworkName, frameworkVersion] = params.frameworkKey.split('#');
      if (frameworkName) queryParams.frameworkName = frameworkName;
      if (frameworkVersion) queryParams.frameworkVersion = frameworkVersion;
    }
    if (params?.controlKey) {
      const parts = params.controlKey.split('#');
      if (parts.length >= 3) {
        const [frameworkName, frameworkVersion, controlId] = parts;
        queryParams.controlId = controlId;
        if (!queryParams.frameworkName) {
          queryParams.frameworkName = frameworkName;
        }
        if (!queryParams.frameworkVersion) {
          queryParams.frameworkVersion = frameworkVersion;
        }
      } else {
        queryParams.controlId = params.controlKey;
      }
    }
    if (params?.status) {
      const mappingStatus = this.toApiMappingStatus(params.status);
      if (mappingStatus) {
        queryParams.mappingStatus = mappingStatus;
      }
    }
    if (params?.limit) {
      queryParams.maxResults = params.limit.toString();
    }
    if (params?.nextToken) {
      queryParams.nextToken = params.nextToken;
    }

    const response = await this.signedFetch('GET', '/v1/mapping/getMappings', queryParams);
    const body = response.body || response;
    const rawMappings = body.Mappings || body.mappings || [];
    const mappings = rawMappings.map((mapping: ControlMapping) => this.normalizeMapping(mapping));
    return {
      mappings,
      count: body.count || mappings.length || 0,
      nextToken:
        body.NextToken || body.nextToken || body.PaginationContext?.nextToken || body.PaginationContext?.NextToken,
    };
  }

  /**
   * Get a specific mapping by mappingKey
   * GET /v1/mapping/{mappingId}
   */
  async getMapping(mappingId: string): Promise<ControlMapping> {
    const response = await this.signedFetch('GET', `/v1/mapping/${encodeURIComponent(mappingId)}`);
    const body = response.body || response;
    const mapping = body.Mapping || body.mapping || body;
    return this.normalizeMapping(mapping);
  }

  /**
   * GET /v1/mapping/getMappings?controlId={}&frameworkName={}&frameworkVersion={}&mappingStatus={}&nextToken={}&maxResults={}
   */
  async getMappingsForControl(
    controlId: string,
    params?: { status?: string; frameworkName?: string; frameworkVersion?: string; maxResults?: number; nextToken?: string }
  ): Promise<MappingsForControlResponse> {
    const queryParams: Record<string, string> = {};
    let resolvedControlId = controlId;
    if (controlId.includes('#')) {
      const parts = controlId.split('#');
      if (parts.length >= 3) {
        const [frameworkName, frameworkVersion, parsedControlId] = parts;
        resolvedControlId = parsedControlId || controlId;
        if (!params?.frameworkName) {
          queryParams.frameworkName = frameworkName;
        }
        if (!params?.frameworkVersion) {
          queryParams.frameworkVersion = frameworkVersion;
        }
      }
    }
    queryParams.controlId = resolvedControlId;

    if (params?.frameworkName) {
      queryParams.frameworkName = params.frameworkName;
    }
    if (params?.frameworkVersion) {
      queryParams.frameworkVersion = params.frameworkVersion;
    }
    if (params?.status) {
      const mappingStatus = this.toApiMappingStatus(params.status);
      if (mappingStatus) {
        queryParams.mappingStatus = mappingStatus;
      }
    }
    if (params?.maxResults) {
      queryParams.maxResults = params.maxResults.toString();
    }
    if (params?.nextToken) {
      queryParams.nextToken = params.nextToken;
    }

    const response = await this.signedFetch('GET', '/v1/mapping/getMappings', queryParams);
    const body = response.body || response;
    const rawMappings = body.Mappings || body.mappings || [];
    const mappings = Array.isArray(rawMappings) ? rawMappings : [];
    return {
      controlId: resolvedControlId,
      mappings: mappings.map((mapping: ControlMapping) => this.normalizeMapping(mapping)),
      count: mappings.length,
      nextToken:
        body.NextToken ||
        body.nextToken ||
        body.PaginationContext?.nextToken ||
        body.PaginationContext?.NextToken,
    };
  }

  // ==========================================================================
  // Frameworks API
  // ==========================================================================

  /**
   * List all frameworks
   * GET /v1/framework
   */
  async listFrameworks(params?: {
    status?: string;
    maxResults?: number;
    nextToken?: string
  }): Promise<ListFrameworksResponse> {
    const queryParams: Record<string, string> = {};

    if (params?.status) {
      queryParams.status = this.toApiLifecycleStatus(params.status) || params.status;
    }
    if (params?.maxResults) {
      queryParams.maxResults = params.maxResults.toString();
    }
    if (params?.nextToken) {
      queryParams.nextToken = params.nextToken;
    }

    const response = await this.signedFetch('GET', '/v1/framework', queryParams);
    const body = response.body || response;
    const rawFrameworks = body.Frameworks || body.frameworks || [];
    const frameworks = rawFrameworks.map((framework: Framework) =>
      this.normalizeFramework(framework)
    );
    return {
      frameworks,
      count: frameworks.length,
      nextToken:
        body.NextToken || body.nextToken || body.PaginationContext?.nextToken || body.PaginationContext?.NextToken,
    };
  }

  /**
   * List all versions of a specific framework
   * GET /v1/framework/{frameworkName}
   */
  async listFrameworkVersions(
    frameworkName: string,
    params?: { maxResults?: number; nextToken?: string }
  ): Promise<ListFrameworksResponse> {
    const queryParams: Record<string, string> = {};
    if (params?.maxResults) {
      queryParams.maxResults = params.maxResults.toString();
    }
    if (params?.nextToken) {
      queryParams.nextToken = params.nextToken;
    }
    const response = await this.signedFetch(
      'GET',
      `/v1/framework/${encodeURIComponent(frameworkName)}`,
      queryParams
    );
    const body = response.body || response;
    const rawFrameworks = body.Frameworks || body.frameworks || [];
    const frameworks = rawFrameworks.map((framework: Framework) =>
      this.normalizeFramework(framework)
    );
    return {
      frameworks,
      count: frameworks.length,
      nextToken:
        body.NextToken || body.nextToken || body.PaginationContext?.nextToken || body.PaginationContext?.NextToken,
    };
  }

  /**
   * Get a specific framework by name and version
   * GET /v1/framework/{frameworkName}/{frameworkVersion}
   */
  async getFramework(frameworkName: string, frameworkVersion: string): Promise<Framework> {
    const response = await this.signedFetch(
      'GET',
      `/v1/framework/${encodeURIComponent(frameworkName)}/${encodeURIComponent(frameworkVersion)}`
    );
    const body = response.body || response;
    const framework =
      body.Framework ||
      body.framework ||
      body.FrameworkInfo ||
      body.frameworkInfo ||
      body;
    return this.normalizeFramework({
      ...framework,
      FrameworkName: body.FrameworkName || frameworkName,
      FrameworkVersion: body.FrameworkVersion || frameworkVersion,
      FrameworkInfo: body.FrameworkInfo || body.frameworkInfo,
    });
  }

  // ==========================================================================
  // Controls API
  // ==========================================================================

  /**
   * GET /v1/control/{frameworkName}/{frameworkVersion}/controls?status={}&nextToken={}&maxResults={}
   */
  async listControls(
    frameworkName: string,
    frameworkVersion: string,
    params?: { maxResults?: number; nextToken?: string; status?: string }
  ): Promise<ListControlsResponse> {
    const queryParams: Record<string, string> = {};

    if (params?.status) {
      queryParams.status = this.toApiLifecycleStatus(params.status) || params.status;
    }
    if (params?.maxResults) {
      queryParams.maxResults = params.maxResults.toString();
    }
    if (params?.nextToken) {
      queryParams.nextToken = params.nextToken;
    }

    const path = `/v1/control/${encodeURIComponent(frameworkName)}/${encodeURIComponent(frameworkVersion)}/controls`;
    const response = await this.signedFetch('GET', path, queryParams);
    const body = response.body || response;
    const rawControls = body.Controls || body.controls || [];
    const controls = rawControls.map((control: FrameworkControl) =>
      this.normalizeFrameworkControl(control, frameworkName, frameworkVersion)
    );
    return {
      controls,
      count: controls.length,
      nextToken:
        body.NextToken || body.nextToken || body.PaginationContext?.nextToken || body.PaginationContext?.NextToken,
    };
  }

  /**
   * Get a specific control
   * GET /v1/control/{frameworkName}/{frameworkVersion}/controls/{controlId}
   */
  async getControl(
    frameworkName: string,
    frameworkVersion: string,
    controlId: string
  ): Promise<FrameworkControl> {
    const response = await this.signedFetch(
      'GET',
      `/v1/control/${encodeURIComponent(frameworkName)}/${encodeURIComponent(frameworkVersion)}/controls/${encodeURIComponent(controlId)}`
    );
    const body = response.body || response;
    const control = body.Control || body.control || body;
    return this.normalizeFrameworkControl(control, frameworkName, frameworkVersion);
  }

  // ==========================================================================
  // Helper Methods for UI Components
  // ==========================================================================

  /**
   * Get approved mappings with pagination
   * Convenience method for ViewMappings component
   */
  async getApprovedMappings(params?: {
    limit?: number;
    nextToken?: string;
    frameworkKey?: string;
    controlKey?: string;
  }): Promise<ListMappingsResponse> {
    if (params?.frameworkKey || params?.controlKey) {
      return this.listMappings({
        status: 'approved' as any,
        ...params,
      });
    }

    const mappings = await this.listMappingsAcrossCommonControls('approved');
    const limitedMappings = params?.limit ? mappings.slice(0, params.limit) : mappings;
    return {
      mappings: limitedMappings,
      count: limitedMappings.length,
    };
  }

  /**
   * Get all mappings with their reviews (for ViewMappings component)
   * This method fetches approved mappings from the API
   */
  async getAllMappingsWithReviews(params?: MappingQueryParams): Promise<MappingWithReviews[]> {
    try {
      const allMappings = await this.listMappingsWithScope(params);
      const limitedMappings = params?.limit ? allMappings.slice(0, params.limit) : allMappings;

      const mappingsWithReviews: MappingWithReviews[] = [];
      const batchSize = 10;

      for (let i = 0; i < limitedMappings.length; i += batchSize) {
        const batch = limitedMappings.slice(i, i + batchSize);
        const batchResults = await Promise.all(
          batch.map(async (mapping) => {
            try {
              const reviews = await this.getReviewsForMapping(mapping.mappingKey, mapping);
              return {
                mapping,
                reviews,
                feedback: undefined,
              };
            } catch (error) {
              console.warn(`Failed to fetch reviews for ${mapping.mappingKey}:`, error);
              return {
                mapping,
                reviews: [],
                feedback: undefined,
              };
            }
          })
        );
        mappingsWithReviews.push(...batchResults);
      }

      return mappingsWithReviews;
    } catch (error) {
      console.error('Error fetching mappings from API:', error);
      throw error;
    }
  }

  // ==========================================================================
  // Reviews API
  // ==========================================================================

  /**
   * List reviews for a mapping with pagination
   * GET /v1/review/{mappingId}/reviews
   */
  async listReviewsForMapping(
    mappingId: string,
    params?: { maxResults?: number; nextToken?: string }
  ): Promise<{ mappingKey: string; reviews: any[]; count: number; nextToken?: string }> {
    const queryParams: Record<string, string> = {};
    if (params?.maxResults) {
      queryParams.maxResults = params.maxResults.toString();
    }
    if (params?.nextToken) {
      queryParams.nextToken = params.nextToken;
    }

    const response = await this.signedFetch(
      'GET',
      `/v1/review/${encodeURIComponent(mappingId)}/reviews`,
      queryParams
    );
    const body = response.body || response;
    const reviews = body.Reviews || body.reviews || [];
    const nextToken =
      body.NextToken ||
      body.nextToken ||
      body.PaginationContext?.nextToken ||
      body.PaginationContext?.NextToken;
    return {
      mappingKey: body.MappingId || body.mappingKey || mappingId,
      reviews,
      count: body.count || reviews.length || 0,
      nextToken,
    };
  }

  /**
   * Query reviews for a specific mapping
   * Matches dynamoDBService signature
   */
  async getReviewsForMapping(
    mappingKey: string,
    mapping?: ControlMapping
  ): Promise<MappingReview[]> {
    const response = await this.listReviewsForMapping(mappingKey);
    return response.reviews.map((review) => this.normalizeMappingReview(review, mapping));
  }

  /**
   * Query reviews by reviewer (best-effort client-side filter)
   * This is expensive without a backend reviewer index endpoint.
   */
  async getReviewsByReviewer(
    params: ReviewQueryParams
  ): Promise<DynamoDBQueryResponse<MappingReview>> {
    if (!params.reviewerId) {
      return {
        items: [],
        count: 0,
        scannedCount: 0,
      };
    }

    const items: MappingReview[] = [];
    let scannedCount = 0;
    const limit = params.limit || 100;

    const applyFilters = (review: MappingReview) => {
      if (review.reviewerId !== params.reviewerId) {
        return false;
      }
      if (params.startDate) {
        const reviewDate = new Date(review.submittedAt).getTime();
        const start = new Date(params.startDate).getTime();
        if (reviewDate < start) {
          return false;
        }
      }
      if (params.endDate) {
        const reviewDate = new Date(review.submittedAt).getTime();
        const end = new Date(params.endDate).getTime();
        if (reviewDate > end) {
          return false;
        }
      }
      return true;
    };

    if (params.mappingKey) {
      const reviews = await this.getReviewsForMapping(params.mappingKey);
      scannedCount = reviews.length;
      for (const review of reviews) {
        if (applyFilters(review)) {
          items.push(review);
          if (items.length >= limit) {
            break;
          }
        }
      }

      return {
        items,
        count: items.length,
        scannedCount,
      };
    }

    // Fetch scoped mappings and short-circuit when limit reached
    const mappings = await this.listMappingsAcrossCommonControls();
    const batchSize = 10;

    for (let i = 0; i < mappings.length; i += batchSize) {
      const batch = mappings.slice(i, i + batchSize);
      const batchResults = await Promise.all(
        batch.map(async (mapping) => {
          try {
            const reviews = await this.getReviewsForMapping(mapping.mappingKey, mapping);
            return reviews;
          } catch (error) {
            console.warn(`Failed to fetch reviews for ${mapping.mappingKey}:`, error);
            return [];
          }
        })
      );

      for (const reviewList of batchResults) {
        scannedCount += reviewList.length;
        for (const review of reviewList) {
          if (!applyFilters(review)) {
            continue;
          }
          items.push(review);
          if (items.length >= limit) {
            return {
              items,
              count: items.length,
              scannedCount,
            };
          }
        }
      }
    }

    return {
      items,
      count: items.length,
      scannedCount,
    };
  }

  /**
   * Submit a new review
   * POST /v1/review/{mappingId}/createReview
   */
  async submitReview(review: Omit<MappingReview, 'reviewKey' | 'submittedAt'>): Promise<void> {
    const decision = review.decision?.toLowerCase();
    if (decision !== 'approve' && decision !== 'reject' && decision !== 'final-approve' && decision !== 'final-reject') {
      throw new Error('review.decision must be approve or reject');
    }

    const isFinalReview = decision === 'final-approve' || decision === 'final-reject';
    const normalizedDecision = decision === 'approve' || decision === 'final-approve' ? 'APPROVE' : 'REJECT';

    await this.signedFetch(
      'POST',
      `/v1/review/${encodeURIComponent(review.mappingKey)}/createReview`,
      undefined,
      {
        Decision: normalizedDecision,
        IsFinalReview: isFinalReview,
        Feedback: {
          Comment: review.justification || '',
          Format: 'PLAIN_TEXT',
        },
      }
    );
  }

  // ==========================================================================
  // Suggestions & Labeling (parity with dynamoDBService)
  // ==========================================================================

  /**
   * Suggest a new mapping
   */
  async suggestMapping(params: {
    controlKey: string;
    mappedControlKey: string;
    reviewerId: string;
    reviewerName: string;
    justification: string;
  }): Promise<void> {
    throw new Error('Suggestion flow is not supported by the current API contract');
  }

  /**
   * Submit a community label for a mapping
   */
  async submitLabel(params: {
    mappingKey: string;
    controlKey: string;
    mappedControlKey: string;
    reviewerId: string;
    reviewerName: string;
    decision: 'label-yes' | 'label-no';
    feedback?: string;
  }): Promise<void> {
    const label = params.decision === 'label-yes' ? 'POSITIVE' : 'NEGATIVE';

    await this.signedFetch(
      'POST',
      `/v1/feedback/${encodeURIComponent(params.mappingKey)}/createFeedback`,
      undefined,
      {
        Label: label,
        Feedback: {
          Comment: params.feedback || '',
          Format: 'PLAIN_TEXT',
        },
      }
    );
  }

  /**
   * Submit final feedback/submission (for ReviewMappings locked feedback)
   * Uses feedback payload to store final submission details.
   */
  async submitFinalFeedback(feedback: Omit<MappingFeedback, 'timestamp'>): Promise<void> {
    await this.signedFetch(
      'POST',
      `/v1/feedback/${encodeURIComponent(feedback.mappingKey)}/createFeedback`,
      undefined,
      {
        Label: 'POSITIVE',
        Feedback: {
          Comment: feedback.justification || '',
          Format: 'PLAIN_TEXT',
        },
      }
    );
  }

  // ==========================================================================
  // Feedback API (for ViewMappings voting)
  // ==========================================================================

  /**
   * Submit feedback (thumbs up/down) for a mapping
   * POST /v1/feedback/{mappingId}/createFeedback
   */
  async submitFeedback(
    mappingId: string,
    data: {
      feedbackProviderId: string;
      label: 'thumbs_up' | 'thumbs_down';
      decision: 'agree' | 'disagree';
      feedback?: string;
    }
  ): Promise<{ message: string; mappingKey: string; feedbackId: string; label: string }> {
    const response = await this.signedFetch(
      'POST',
      `/v1/feedback/${encodeURIComponent(mappingId)}/createFeedback`,
      undefined,
      {
        Label: data.label === 'thumbs_up' ? 'POSITIVE' : 'NEGATIVE',
        Feedback: {
          Comment: data.feedback || '',
          Format: 'PLAIN_TEXT',
        },
      }
    );
    return response.body || response;
  }

  /**
   * Update existing feedback for a mapping
   * PUT /v1/feedback/{mappingId}/feedbacks/{feedbackId}
   */
  async updateFeedback(
    mappingId: string,
    feedbackId: string,
    data: {
      label?: 'thumbs_up' | 'thumbs_down';
      decision?: 'agree' | 'disagree';
      feedback?: string;
    }
  ): Promise<{ message: string; mappingKey: string; feedbackId: string }> {
    const response = await this.signedFetch(
      'PUT',
      `/v1/feedback/${encodeURIComponent(mappingId)}/feedbacks/${encodeURIComponent(feedbackId)}`,
      undefined,
      {
        Label: data.label
          ? data.label === 'thumbs_up'
            ? 'POSITIVE'
            : 'NEGATIVE'
          : undefined,
        Feedback: data.feedback
          ? {
            Comment: data.feedback,
            Format: 'PLAIN_TEXT',
          }
          : undefined,
      }
    );
    return response.body || response;
  }

  /**
   * List feedbacks for a mapping
   * GET /v1/feedback/{mappingId}/feedbacks
   */
  async listFeedbacks(
    mappingId: string,
    params?: { maxResults?: number; nextToken?: string }
  ): Promise<{
    mappingKey: string;
    feedbacks: Array<{
      feedbackId: string;
      mappingKey: string;
      reviewerId: string;
      feedbackProviderId: string;
      label: string;
      decision: string;
      feedback?: string;
      submittedAt: string;
    }>;
    count: number;
    nextToken?: string;
  }> {
    const body = await this.listFeedbacksRaw(mappingId, params);
    const rawFeedbacks = body.Feedbacks || body.feedbacks || [];
    const feedbacks = rawFeedbacks.map((feedback: any) => {
      const label = this.normalizeFeedbackLabel(feedback.FeedbackLabel || feedback.label);
      return {
        feedbackId: feedback.FeedbackId || feedback.feedbackId || feedback.reviewerId,
        mappingKey: feedback.MappingId || feedback.mappingKey || mappingId,
        reviewerId: feedback.ReviewerId || feedback.reviewerId,
        feedbackProviderId:
          feedback.FeedbackProviderId || feedback.feedbackProviderId || feedback.reviewerId,
        label,
        decision: label === 'thumbs_down' ? 'disagree' : 'agree',
        feedback: this.normalizeFeedbackText(feedback.Feedback || feedback.feedback),
        submittedAt: feedback.SubmissionTimestamp || feedback.submittedAt,
      };
    });

    return {
      mappingKey: body.MappingId || body.mappingKey || mappingId,
      feedbacks,
      count: body.count || feedbacks.length || 0,
      nextToken:
        body.NextToken ||
        body.nextToken ||
        body.PaginationContext?.nextToken ||
        body.PaginationContext?.NextToken,
    };
  }

  /**
   * Get current user's feedback for multiple mappings
   * Fetches feedbacks and filters by userId
   */
  async getUserFeedbackForMappings(
    mappingIds: string[],
    userId: string
  ): Promise<Map<string, { label: string; feedback?: string; feedbackId: string }>> {
    const userFeedbackMap = new Map();
    const uniqueMappingIds = Array.from(new Set(mappingIds.filter(Boolean)));

    for (
      let i = 0;
      i < uniqueMappingIds.length;
      i += NexusApiService.FEEDBACK_FETCH_BATCH_SIZE
    ) {
      const batch = uniqueMappingIds.slice(
        i,
        i + NexusApiService.FEEDBACK_FETCH_BATCH_SIZE
      );

      const feedbackPromises = batch.map(async (mappingId) => {
        try {
          const response = await this.listFeedbacks(mappingId);
          const userFeedback = response.feedbacks.find(
            (f) => f.feedbackProviderId === userId || f.reviewerId === userId
          );
          if (userFeedback) {
            userFeedbackMap.set(mappingId, {
              label: userFeedback.label,
              feedback: userFeedback.feedback,
              feedbackId: userFeedback.feedbackId,
            });
          }
        } catch (error) {
          // Silently ignore errors for individual mappings
          console.warn(`Failed to fetch feedback for mapping ${mappingId}:`, error);
        }
      });

      await Promise.all(feedbackPromises);
    }

    return userFeedbackMap;
  }

  // ==========================================================================
  // Framework Controls (parity helpers)
  // ==========================================================================

  async getFrameworkControlsByKey(frameworkKey: string): Promise<FrameworkControl[]> {
    const [frameworkName, frameworkVersion] = frameworkKey.split('#');
    if (!frameworkName || !frameworkVersion) {
      return [];
    }
    const controls: FrameworkControl[] = [];
    let nextToken: string | undefined;

    do {
      const response = await this.listControls(frameworkName, frameworkVersion, {
        maxResults: NexusApiService.DEFAULT_PAGE_SIZE,
        nextToken,
      });
      controls.push(...response.controls);

      const previousToken = nextToken;
      nextToken = response.nextToken;
      if (nextToken && nextToken === previousToken) {
        break;
      }
    } while (nextToken);

    return controls;
  }

  async batchGetFrameworkControls(
    controls: Array<{ frameworkKey: string; controlKey: string }>
  ): Promise<Map<string, FrameworkControl>> {
    const results = new Map<string, FrameworkControl>();
    if (controls.length === 0) {
      return results;
    }

    const uniqueControls = new Map<string, { frameworkKey: string; controlKey: string }>();
    controls.forEach((control) => {
      if (!control.frameworkKey || !control.controlKey) {
        return;
      }
      uniqueControls.set(`${control.frameworkKey}#${control.controlKey}`, control);
    });

    const deduped = Array.from(uniqueControls.values());
    const batchSize = 20;
    for (let i = 0; i < deduped.length; i += batchSize) {
      const batch = deduped.slice(i, i + batchSize);
      const batchResults = await Promise.all(
        batch.map(async (control) => {
          try {
            const [frameworkName, frameworkVersion] = control.frameworkKey.split('#');
            const controlId = control.controlKey.includes('#')
              ? control.controlKey.split('#').slice(-1)[0]
              : control.controlKey;
            const item = await this.getControl(frameworkName, frameworkVersion, controlId);
            return { key: `${control.frameworkKey}#${control.controlKey}`, item };
          } catch (error) {
            console.warn('Failed to fetch control:', control, error);
            return null;
          }
        })
      );

      batchResults.forEach((result) => {
        if (result?.item) {
          results.set(result.key, result.item);
        }
      });
    }

    return results;
  }

  // ==========================================================================
  // Aggregated Data Operations
  // ==========================================================================

  async getMappingWithReviews(mappingKey: string): Promise<MappingWithReviews | null> {
    try {
      const [mapping, reviews, feedbackResponse, userAlias] = await Promise.all([
        this.getMapping(mappingKey).catch(() => null),
        this.getReviewsForMapping(mappingKey).catch(() => []),
        this.listFeedbacksRaw(mappingKey).catch(() => ({ feedbacks: [] })),
        getUserAlias().catch(() => ''),
      ]);

      if (!mapping) {
        return null;
      }

      const cleanAlias = userAlias?.startsWith('amazonfederatemidway_')
        ? userAlias.replace('amazonfederatemidway_', '')
        : userAlias;
      const rawFeedbacks = feedbackResponse.feedbacks || [];
      const normalizedFeedbacks =
        feedbackResponse.Feedbacks ||
        feedbackResponse.feedbacks ||
        feedbackResponse.body?.Feedbacks ||
        rawFeedbacks ||
        [];
      const userFeedback = cleanAlias
        ? normalizedFeedbacks.find((f: any) => {
          const providerId =
            f?.FeedbackProviderId || f?.feedbackProviderId || f?.ReviewerId || f?.reviewerId;
          return providerId === cleanAlias;
        })
        : undefined;
      const lockedFeedback = normalizedFeedbacks.find(
        (f: any) =>
          f?.IsFinalReview === true ||
          f?.isFinalReview === true ||
          f?.feedback?.status === 'locked' ||
          f?.Feedback?.status === 'locked'
      );
      const selectedFeedback = userFeedback || (!cleanAlias ? lockedFeedback : undefined);

      return {
        mapping,
        reviews,
        feedback: selectedFeedback ? this.normalizeMappingFeedback(selectedFeedback) : undefined,
      };
    } catch (error) {
      console.error('Error getting mapping with reviews:', error);
      throw error;
    }
  }

  async getAllMappingsForLabeling(currentUserId: string): Promise<MappingWithReviews[]> {
    try {
      const mappings = await this.listMappingsAcrossCommonControls('LABELING_IN_PROGRESS');

      const mappingIds = mappings.map((m) => m.mappingKey);
      const userFeedbackMap = await this.getUserFeedbackForMappings(mappingIds, currentUserId);

      return mappings.map((mapping) => ({
        mapping,
        reviews: [],
        feedback: userFeedbackMap.get(mapping.mappingKey) as any,
      }));
    } catch (error) {
      console.error('Error getting mappings for labeling:', error);
      throw error;
    }
  }


  /**
   * Get Common Controls with extracted hierarchy for cascading filters
   * Fetches all controls from Common-Controls framework and extracts unique
   * domains (L1), objectives (L2), and controls (L3) with their relationships
   *
   * @returns Structured hierarchy data for UI cascading dropdowns
   */
  async getCommonControlsWithHierarchy(): Promise<CommonControlsHierarchyResponse> {
    // Fetch all active controls from Common-Controls framework with pagination
    const allControls = await this.listAllCommonControls();

    // Maps for deduplication
    const domainsMap = new Map<string, ControlDomain>();
    const objectivesMap = new Map<string, ControlObjective>();
    const controls: CommonControl[] = [];

    // Process each control and extract hierarchy
    for (const control of allControls) {
      const hierarchy = this.extractHierarchy(control);
      if (!hierarchy) {
        console.warn(`Control ${control.controlKey} has no hierarchy data`);
        continue;
      }

      // Add domain (L1) if not already present
      if (hierarchy.l1 && !domainsMap.has(hierarchy.l1.code)) {
        domainsMap.set(hierarchy.l1.code, {
          code: hierarchy.l1.code,
          title: hierarchy.l1.title,
          description: hierarchy.l1.description || '',
        });
      }

      // Add objective (L2) if not already present
      if (hierarchy.l2 && !objectivesMap.has(hierarchy.l2.code)) {
        objectivesMap.set(hierarchy.l2.code, {
          code: hierarchy.l2.code,
          title: hierarchy.l2.title,
          description: hierarchy.l2.description || '',
          domainCode: hierarchy.l1?.code || '',
        });
      }

      // Add control (L3)
      controls.push({
        controlKey: control.controlKey,
        code: hierarchy.l3?.code || control.controlId || '',
        title: hierarchy.l3?.title || control.title || '',
        description: hierarchy.l3?.description || control.description || '',
        objectiveCode: hierarchy.l2?.code || '',
        domainCode: hierarchy.l1?.code || '',
        hierarchy,
      });
    }

    // Sort results alphabetically by title for consistent UI display
    const domains = Array.from(domainsMap.values()).sort((a, b) =>
      a.title.localeCompare(b.title)
    );
    const objectives = Array.from(objectivesMap.values()).sort((a, b) =>
      a.title.localeCompare(b.title)
    );
    controls.sort((a, b) => a.title.localeCompare(b.title));

    return { domains, objectives, controls };
  }

  private async listAllCommonControls(): Promise<FrameworkControl[]> {
    const controls: FrameworkControl[] = [];
    let nextToken: string | undefined;

    do {
      const response = await this.listControls(
        NexusApiService.COMMON_CONTROLS_FRAMEWORK_NAME,
        NexusApiService.COMMON_CONTROLS_FRAMEWORK_VERSION,
        {
          status: 'active',
          maxResults: NexusApiService.DEFAULT_PAGE_SIZE,
          nextToken,
        }
      );
      controls.push(...response.controls);
      nextToken = response.nextToken;
    } while (nextToken);

    return controls;
  }

  private async listMappingsForControlKey(
    controlKey: string,
    status?: MappingStatus | string
  ): Promise<ControlMapping[]> {
    const parts = controlKey.split('#');
    if (parts.length < 3) {
      return [];
    }

    const frameworkName = parts[0];
    const frameworkVersion = parts[1];
    const controlId = parts.slice(2).join('#');

    const mappings: ControlMapping[] = [];
    let nextToken: string | undefined;

    do {
      const response = await this.getMappingsForControl(controlId, {
        frameworkName,
        frameworkVersion,
        status,
        maxResults: NexusApiService.DEFAULT_PAGE_SIZE,
        nextToken,
      });
      mappings.push(...response.mappings);
      nextToken = response.nextToken;
    } while (nextToken);

    return mappings;
  }

  private async listMappingsAcrossCommonControls(
    status?: MappingStatus | string
  ): Promise<ControlMapping[]> {
    const controls = await this.listAllCommonControls();
    const controlKeys = Array.from(
      new Set(
        controls
          .map((control) => control.controlKey)
          .filter((key): key is string => Boolean(key))
      )
    );

    const allMappings: ControlMapping[] = [];
    for (let i = 0; i < controlKeys.length; i += NexusApiService.CONTROL_FETCH_BATCH_SIZE) {
      const batch = controlKeys.slice(i, i + NexusApiService.CONTROL_FETCH_BATCH_SIZE);
      const batchResults = await Promise.all(
        batch.map(async (controlKey) => {
          try {
            return await this.listMappingsForControlKey(controlKey, status);
          } catch (error) {
            console.warn(`Failed to fetch mappings for controlKey ${controlKey}:`, error);
            return [];
          }
        })
      );
      batchResults.forEach((mappings) => allMappings.push(...mappings));
    }

    const uniqueMappings = new Map<string, ControlMapping>();
    for (const mapping of allMappings) {
      const key = mapping.mappingKey || `${mapping.controlKey}#${mapping.mappedControlKey}`;
      if (!key) {
        continue;
      }
      uniqueMappings.set(key, mapping);
    }

    return Array.from(uniqueMappings.values());
  }

  private async listMappingsWithScope(params?: MappingQueryParams): Promise<ControlMapping[]> {
    if (params?.frameworkKey || params?.controlKey) {
      const mappings: ControlMapping[] = [];
      let nextToken = params.nextToken;

      do {
        const response = await this.listMappings({
          ...params,
          limit: params?.limit || NexusApiService.DEFAULT_PAGE_SIZE,
          nextToken,
        });
        mappings.push(...response.mappings);
        nextToken = response.nextToken;
      } while (nextToken);

      return mappings;
    }

    return this.listMappingsAcrossCommonControls(params?.status);
  }

  /**
   * Extract hierarchy data from a FrameworkControl's additionalInfo field
   * The hierarchy is stored in additionalInfo as a DynamoDB List structure
   *
   * @param control - Framework control with additionalInfo containing hierarchy
   * @returns Extracted ControlHierarchy or null if not present
   */
  private extractHierarchy(control: FrameworkControl): ControlHierarchy | null {
    try {
      const additionalInfo = control.additionalInfo;
      if (!additionalInfo) return null;

      // Handle different possible structures from DynamoDB
      // Structure 1: additionalInfo is an array with objects containing data.hierarchy
      // Structure 2: additionalInfo.hierarchy directly exists
      let hierarchyData: any = null;

      if (Array.isArray(additionalInfo)) {
        // DynamoDB List format: additionalInfo[0].data.hierarchy
        const firstItem = additionalInfo[0];
        if (firstItem?.data?.hierarchy) {
          hierarchyData = firstItem.data.hierarchy;
        } else if (firstItem?.M?.data?.M?.hierarchy) {
          // Raw DynamoDB format with type markers
          hierarchyData = this.parseDynamoDBMap(firstItem.M.data.M.hierarchy);
        }
      } else if (additionalInfo.hierarchy) {
        // Direct hierarchy object
        hierarchyData = additionalInfo.hierarchy;
      } else if (additionalInfo.L) {
        // Raw DynamoDB List format
        const firstItem = additionalInfo.L[0];
        if (firstItem?.M?.data?.M?.hierarchy) {
          hierarchyData = this.parseDynamoDBMap(firstItem.M.data.M.hierarchy);
        }
      }

      if (!hierarchyData) return null;

      // Extract L1, L2, L3 levels
      return {
        l1: this.extractHierarchyLevel(hierarchyData.l1 || hierarchyData.L1),
        l2: this.extractHierarchyLevel(hierarchyData.l2 || hierarchyData.L2),
        l3: this.extractHierarchyLevel(hierarchyData.l3 || hierarchyData.L3),
      };
    } catch (error) {
      console.warn(`Failed to extract hierarchy for control ${control.controlKey}:`, error);
      return null;
    }
  }

  /**
   * Extract a single hierarchy level from potentially DynamoDB-formatted data
   */
  private extractHierarchyLevel(levelData: any): { code: string; title: string; description: string } {
    if (!levelData) {
      return { code: '', title: '', description: '' };
    }

    // Handle raw DynamoDB map format with type markers (M, S, etc.)
    if (levelData.M) {
      return {
        code: levelData.M.code?.S || '',
        title: levelData.M.title?.S || '',
        description: levelData.M.description?.S || '',
      };
    }

    // Handle already-parsed object format
    return {
      code: levelData.code || '',
      title: levelData.title || '',
      description: levelData.description || '',
    };
  }

  /**
   * Parse a DynamoDB Map structure into a plain JavaScript object
   * Handles nested M (Map) and S (String) type markers
   */
  private parseDynamoDBMap(dynamoMap: any): any {
    if (!dynamoMap) return null;

    if (dynamoMap.M) {
      const result: Record<string, any> = {};
      for (const [key, value] of Object.entries(dynamoMap.M)) {
        result[key] = this.parseDynamoDBMap(value);
      }
      return result;
    }

    if (dynamoMap.S) {
      return dynamoMap.S;
    }

    if (dynamoMap.N) {
      return Number(dynamoMap.N);
    }

    if (dynamoMap.L) {
      return dynamoMap.L.map((item: any) => this.parseDynamoDBMap(item));
    }

    // Already a plain value or object
    if (typeof dynamoMap === 'object' && dynamoMap !== null) {
      const result: Record<string, any> = {};
      for (const [key, value] of Object.entries(dynamoMap)) {
        result[key] = this.parseDynamoDBMap(value);
      }
      return result;
    }

    return dynamoMap;
  }
}

// Singleton instance
export const nexusApiService = new NexusApiService();
